import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import ProgressBar from '../common/ProgressBar';
import QuestionSection from './QuestionSection';
import { evaluationAPI, handleApiError } from '../../services/api';
import toast from 'react-hot-toast';

const EvaluationForm = ({ evaluationData, questions, onComplete }) => {
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasStarted, setHasStarted] = useState(false); // New state to track if evaluation has started

  // Initialize responses from existing data
  useEffect(() => {
    if (evaluationData?.existingResponses) {
      const existingResponses = {};
      evaluationData.existingResponses.forEach(response => {
        existingResponses[response.questionId] = response.value;
      });
      setResponses(existingResponses);
    }
  }, [evaluationData]);

  // Auto-save progress every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (Object.keys(responses).length > 0) {
        saveProgress(true); // silent save
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [responses]);

  const currentCategory = questions[currentCategoryIndex];
  const totalQuestions = questions.reduce((sum, cat) => sum + cat.questions.length, 0);
  const answeredQuestions = Object.keys(responses).length;
  const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const saveProgress = async (silent = false) => {
    if (Object.keys(responses).length === 0) return;

    setLoading(true);
    try {
      const responsesArray = Object.entries(responses).map(([questionId, value]) => ({
        questionId,
        value: value.toString()
      }));

      await evaluationAPI.saveProgress({
        token: evaluationData.token,
        responses: responsesArray
      });

      setLastSaved(new Date());
      if (!silent) {
        toast.success('Progress saved successfully');
      }
    } catch (error) {
      if (!silent) {
        handleApiError(error, 'Failed to save progress');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Validate all required questions are answered - handle both field name formats
    const allRequiredQuestions = questions.flatMap(cat =>
      cat.questions.filter(q => {
        // Handle both isRequired and isReq field names due to truncation
        const isRequired = q.isRequired !== undefined ? q.isRequired : q.isReq;
        return isRequired;
      }).map(q => q.id)
    );

    const missingAnswers = allRequiredQuestions.filter(qId => !responses[qId]);

    if (missingAnswers.length > 0) {
      toast.error(`Please answer all required questions (${missingAnswers.length} remaining)`);
      return;
    }

    setLoading(true);
    try {
      const responsesArray = Object.entries(responses).map(([questionId, value]) => ({
        questionId,
        value: value.toString()
      }));

      await evaluationAPI.submitEvaluation({
        token: evaluationData.token,
        responses: responsesArray
      });

      toast.success('Evaluation submitted successfully!');
      onComplete();
    } catch (error) {
      handleApiError(error, 'Failed to submit evaluation');
    } finally {
      setLoading(false);
    }
  };

  const canGoNext = currentCategoryIndex < questions.length - 1;
  const canGoPrev = currentCategoryIndex > 0;
  const isLastCategory = currentCategoryIndex === questions.length - 1;

  // Check if current category is complete
  const currentCategoryQuestions = currentCategory?.questions || [];
  const currentCategoryAnswered = currentCategoryQuestions.filter(q => responses[q.id]).length;
  const isCategoryComplete = currentCategoryAnswered === currentCategoryQuestions.length;

  // Handle next section navigation with validation
  const handleNextSection = () => {
    if (!isCategoryComplete) {
      // Show user-friendly message
      toast.error(
        `Please complete all questions in this section before proceeding. You have answered ${currentCategoryAnswered} out of ${currentCategoryQuestions.length} questions.`,
        {
          duration: 4000,
          icon: '📝',
        }
      );
      return;
    }

    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Move to next section
    setCurrentCategoryIndex(prev => prev + 1);
  };

  // Handle previous section navigation
  const handlePrevSection = () => {
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Move to previous section
    setCurrentCategoryIndex(prev => prev - 1);
  };

  if (!currentCategory) {
    return <div className="text-center py-8 text-secondary-600">Loading questions...</div>;
  }

  // Welcome Screen - shown before starting the evaluation
  if (!hasStarted) {
    return (
      <div className="max-w-3xl mx-auto">
        {/* Welcome Header — light and airy */}
        <div className="text-center mb-10 pt-4">
          <p className="text-sm font-medium text-primary-600 uppercase tracking-widest mb-3">
            The {evaluationData.family?.name} Family
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-semibold text-secondary-800 mb-4 leading-tight">
            Welcome, {evaluationData.familyMember.firstName}
          </h1>
          <p className="text-lg text-secondary-500 max-w-xl mx-auto leading-relaxed">
            This assessment captures each family member's perspective on wealth, values, and legacy — helping your family align on what matters most.
          </p>
        </div>

        {/* Three info pills in a row */}
        <div className="flex flex-wrap justify-center gap-3 mb-10">
          <div className="bg-secondary-500/60 rounded-full px-5 py-2.5 flex items-center space-x-2.5">
            <svg className="w-4 h-4 text-white/90" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
            <span className="text-sm font-medium text-white">15–20 minutes</span>
          </div>
          <div className="bg-secondary-500/60 rounded-full px-5 py-2.5 flex items-center space-x-2.5">
            <svg className="w-4 h-4 text-white/90" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
            </svg>
            <span className="text-sm font-medium text-white">Completely confidential</span>
          </div>
          <div className="bg-secondary-500/60 rounded-full px-5 py-2.5 flex items-center space-x-2.5">
            <svg className="w-4 h-4 text-white/90" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 0 0 4.5 4.5H18a3.75 3.75 0 0 0 1.332-7.257 3 3 0 0 0-3.758-3.848 5.25 5.25 0 0 0-10.233 2.33A4.502 4.502 0 0 0 2.25 15Z" />
            </svg>
            <span className="text-sm font-medium text-white">Auto-saved progress</span>
          </div>
        </div>

        {/* Category Preview — clean cards */}
        <div className="mb-10">
          <h3 className="font-serif text-xl font-semibold text-secondary-800 text-center mb-6">
            Your assessment at a glance
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {questions.map((category, index) => {
              const isLast = index === questions.length - 1;
              const isOddLast = isLast && questions.length % 2 !== 0;
              return (
                <div
                  key={category.id}
                  className={`bg-white/60 backdrop-blur-md border border-secondary-200/50 rounded-xl px-5 py-4 hover:shadow-md transition-all duration-200 group ${
                    isOddLast ? 'sm:col-span-2' : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xs font-bold text-primary-600 bg-primary-100 w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </span>
                    <h5 className="text-sm font-semibold text-secondary-700 group-hover:text-primary-700 transition-colors">
                      {category.name}
                    </h5>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mb-8">
          <button
            onClick={() => setHasStarted(true)}
            className="group bg-primary-500 hover:bg-primary-600 text-white font-medium px-10 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-flex items-center"
          >
            Begin Assessment
            <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </button>
          <p className="text-secondary-400 mt-3 text-sm">
            Your answers are saved automatically as you go
          </p>
        </div>

        {/* Family Members Section */}
        {evaluationData.familyMembers && evaluationData.familyMembers.length > 0 && (
          <div className="border-t border-secondary-200/50 pt-8">
            <p className="text-center text-sm text-secondary-500 mb-4">
              Family members participating in this assessment
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {evaluationData.familyMembers.map((member) => (
                <div
                  key={member.id}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    member.isCurrentUser
                      ? 'bg-primary-100 text-primary-700 border border-primary-200'
                      : 'bg-secondary-100 text-secondary-600 border border-secondary-200/50'
                  }`}
                >
                  {member.firstName} {member.lastName}
                  {member.isCurrentUser && (
                    <span className="ml-1.5 text-xs text-primary-500">(You)</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Questionnaire View - shown after clicking "Let's Begin"
  return (
    <div className="max-w-4xl mx-auto">
      {/* Clean Header */}
      <div className="mb-8">
        {/* Title row */}
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <h1 className="font-serif text-2xl font-semibold text-secondary-800">
              Family Legacy Assessment
            </h1>
            <p className="text-sm text-secondary-400 mt-0.5">
              {evaluationData.familyMember.firstName} {evaluationData.familyMember.lastName} • {evaluationData.family?.name}
            </p>
          </div>
          <span className="text-sm font-medium text-secondary-500">
            {answeredQuestions} of {totalQuestions}
          </span>
        </div>

        {/* Thin progress bar */}
        <div className="mb-5">
          <ProgressBar progress={progressPercentage} size="small" />
        </div>

        {/* Category Navigation Pills */}
        <div className="flex flex-wrap gap-2">
          {questions.map((category, index) => {
            const categoryAnswered = category.questions.filter(q => responses[q.id]).length;
            const categoryTotal = category.questions.length;
            const isComplete = categoryAnswered === categoryTotal;
            const isCurrent = index === currentCategoryIndex;
            const isAccessible = index <= currentCategoryIndex || isComplete;

            return (
              <button
                key={category.id}
                onClick={() => {
                  if (isAccessible) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    setCurrentCategoryIndex(index);
                  }
                }}
                disabled={!isAccessible}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                  isCurrent
                    ? 'bg-primary-500 text-white shadow-sm'
                    : isComplete
                    ? 'bg-primary-100 text-primary-700 border border-primary-200 cursor-pointer hover:bg-primary-200'
                    : isAccessible
                    ? 'bg-secondary-100 text-secondary-500 cursor-pointer hover:bg-secondary-200'
                    : 'bg-secondary-50 text-secondary-300 cursor-default'
                }`}
              >
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Current Category Questions - Enhanced */}
      <div className="bg-white/70 backdrop-blur-md rounded-xl shadow-lg border border-secondary-200/50 overflow-hidden">
        <div className="px-4 sm:px-6 py-5 border-b border-secondary-200/50">
          <h2 className="font-serif text-xl font-semibold text-secondary-800">
            {currentCategory.name}
          </h2>
          {currentCategory.description && (
            <p className="text-secondary-500 text-sm mt-1">
              {currentCategory.description}
            </p>
          )}
        </div>

        <div className="px-4 sm:px-6 py-6">
          <QuestionSection
            category={currentCategory}
            responses={responses}
            onResponseChange={handleResponseChange}
          />
        </div>

        {/* Navigation Footer - Enhanced */}
        <div className="px-4 sm:px-6 py-5 border-t border-secondary-200/50 bg-secondary-50/50 backdrop-blur-sm">
          <div className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <button
                onClick={canGoPrev ? handlePrevSection : undefined}
                disabled={!canGoPrev}
                className="disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center px-4 py-2 rounded-lg border border-secondary-300 text-secondary-700 bg-white hover:bg-secondary-50 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <ChevronLeftIcon className="w-4 h-4 mr-2" />
                Previous Section
              </button>

              <button
                onClick={() => saveProgress()}
                disabled={loading}
                className="inline-flex items-center px-4 py-2 rounded-lg border border-primary-300 text-primary-700 bg-white hover:bg-primary-50 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0 3 3m-3-3-3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z" />
                </svg>
                {loading ? 'Saving...' : 'Save Progress'}
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-4 sm:justify-end">
              {lastSaved && (
                <span className="text-xs text-secondary-500 bg-white/50 px-2 py-1 rounded-full">
                  ✓ Last saved: {lastSaved.toLocaleTimeString()}
                </span>
              )}

              {isLastCategory ? (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-6 sm:px-8 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 inline-flex items-center text-base sm:text-lg font-semibold"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                  </svg>
                  {loading ? 'Submitting...' : 'Submit Evaluation'}
                </button>
              ) : (
                <button
                  onClick={canGoNext ? handleNextSection : undefined}
                  disabled={!canGoNext}
                  className={`bg-primary-500 hover:bg-primary-600 text-white inline-flex items-center px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 ${
                    !isCategoryComplete && canGoNext ? 'animate-pulse' : ''
                  } ${!canGoNext ? 'disabled:opacity-50 disabled:cursor-not-allowed' : ''}`}
                >
                  Next Section
                  <ChevronRightIcon className="w-4 h-4 ml-2" />
                </button>
              )}
            </div>
          </div>

          {/* Progress indicator for current section */}
          {!isCategoryComplete && (
            <div className="mt-4 text-center">
              <div className="bg-accent-50 border border-accent-200 rounded-lg px-4 py-3 inline-flex items-center space-x-2">
                <svg className="w-5 h-5 text-accent-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <p className="text-sm text-accent-800 font-medium">
                  Please complete all questions in this section ({currentCategoryAnswered}/{currentCategoryQuestions.length} answered) before proceeding to the next section
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvaluationForm;
